import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, Alert, IconButton, TextField,
  MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Tooltip, Grid, Switch, FormControlLabel
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add, Edit, Delete, Home, Class as ClassIcon, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useStages } from '../hooks/useStages';

const API = process.env.REACT_APP_API_URL;

const ClassList = () => {
  const navigate = useNavigate();
  const { stages } = useStages();
  const [classes,    setClasses]    = useState([]);
  const [streams,    setStreams]    = useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [stageFilter,setStageFilter]= useState('');
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [confirmDlg, setConfirmDlg] = useState(null);

  const emptyForm = { stageCode: '', streamCode: '', className: '', classTeacher: '', isActive: true };
  const [form, setForm] = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchClasses = useCallback(async () => {
    try {
      const params = stageFilter ? `?stageCode=${stageFilter}` : '';
      const res = await axios.get(`${API}/api/academics/classes${params}`, { headers });
      setClasses(res.data);
    } catch { setError('Failed to load classes'); }
  }, [stageFilter]); // eslint-disable-line

  const fetchStreams = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/academics/streams?active=true`, { headers });
      setStreams(res.data);
    } catch { setStreams([]); }
  }, []); // eslint-disable-line

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/employees?status=Active`, { headers });
      const list = res.data?.data || res.data || [];
      setEmployees(list.map(e => ({
        employeeNo: e.employeeNo,
        label:      `${e.firstName} ${e.lastName} (${e.employeeNo})`,
        name:       `${e.firstName} ${e.lastName}`,
      })));
    } catch { setEmployees([]); }
  }, []); // eslint-disable-line

  useEffect(() => { fetchClasses(); }, [fetchClasses]);
  useEffect(() => { fetchStreams(); fetchEmployees(); }, [fetchStreams, fetchEmployees]);

  // Auto-generate classCode preview from stage + stream
  const derivedCode = form.stageCode
    ? `${form.stageCode}${form.streamCode ? '-' + form.streamCode : ''}`
    : '';

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setError(''); setDialogOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ stageCode: c.stageCode, streamCode: c.streamCode || '', className: c.className, classTeacher: c.classTeacher || '', isActive: c.isActive });
    setError(''); setDialogOpen(true);
  };

  const handle = (e) => {
    const { name, value } = e.target;
    setForm(f => {
      const next = { ...f, [name]: value };
      // Auto-update className when stage or stream changes (only for new records)
      if (!editing && (name === 'stageCode' || name === 'streamCode')) {
        const stageName = stages.find(s => s.code === (name === 'stageCode' ? value : next.stageCode))?.name || '';
        const streamName = streams.find(s => s.streamCode === (name === 'streamCode' ? value : next.streamCode))?.streamName || '';
        next.className = streamName ? `${stageName} ${streamName}` : stageName;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!form.stageCode)        { setError('Stage is required'); return; }
    if (!form.className.trim()) { setError('Class Name is required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await axios.put(`${API}/api/academics/classes/${editing._id}`, form, { headers });
        setSuccess('Class updated');
      } else {
        await axios.post(`${API}/api/academics/classes`, { ...form, classCode: derivedCode }, { headers });
        setSuccess('Class created');
      }
      setDialogOpen(false); fetchClasses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = (c) => setConfirmDlg(c);

  const confirmDelete = async () => {
    const c = confirmDlg;
    setConfirmDlg(null);
    try {
      await axios.delete(`${API}/api/academics/classes/${c._id}`, { headers });
      setSuccess('Deleted'); fetchClasses();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const stageName = (code) => stages.find(s => s.code === code)?.name || code;

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <ClassIcon color="primary" />
              <Typography variant="h5" color="primary">Classes</Typography>
              <Chip label={classes.length} size="small" color="primary" sx={{ ml: 0.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Back to Academics">
                <IconButton onClick={() => navigate('/academics')}
                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Add Class</Button>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          <TextField select size="small" label="Filter by Stage" value={stageFilter}
            onChange={e => setStageFilter(e.target.value)} sx={{ mb: 2, minWidth: 220 }}>
            <MenuItem value="">All Stages</MenuItem>
            {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
          </TextField>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Class Name</strong></TableCell>
                <TableCell><strong>Stage</strong></TableCell>
                <TableCell><strong>Stream</strong></TableCell>
                <TableCell><strong>Class Teacher</strong></TableCell>
                <TableCell align="center"><strong>Status</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {classes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary" py={2}>
                      {stageFilter ? 'No classes for this stage.' : 'No classes defined yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : classes.map(c => (
                <TableRow key={c._id} hover>
                  <TableCell><strong>{c.classCode}</strong></TableCell>
                  <TableCell>{c.className}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.stageCode}</Typography>
                    <Typography variant="caption" color="text.secondary">{stageName(c.stageCode)}</Typography>
                  </TableCell>
                  <TableCell>
                    {c.streamCode
                      ? <Chip label={c.streamCode} size="small" variant="outlined" color="info" />
                      : <Typography variant="caption" color="text.secondary">Single stream</Typography>}
                  </TableCell>
                  <TableCell>
                    {c.classTeacher
                      ? (() => {
                          const emp = employees.find(e => e.employeeNo === c.classTeacher);
                          return <Chip label={emp ? emp.name : c.classTeacher} size="small" variant="outlined" />;
                        })()
                      : <em style={{ color: '#bbb', fontSize: 12 }}>Not assigned</em>}
                  </TableCell>
                  <TableCell align="center">
                    <Chip label={c.isActive ? 'Active' : 'Inactive'}
                      color={c.isActive ? 'success' : 'default'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => openEdit(c)}><Edit fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(c)}><Delete fontSize="small" /></IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── Confirm Delete dialog ── */}
      <Dialog open={Boolean(confirmDlg)} onClose={() => setConfirmDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <WarningAmber color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>Delete class <strong>{confirmDlg?.className}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDlg(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Create / Edit Dialog */}
      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit Class — ${editing.classCode}` : 'Add Class'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField select label="Stage *" name="stageCode" value={form.stageCode}
                onChange={handle} fullWidth size="small" disabled={!!editing}>
                <MenuItem value=""><em>Select stage</em></MenuItem>
                {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField select label="Stream (optional)" name="streamCode" value={form.streamCode}
                onChange={handle} fullWidth size="small" disabled={!!editing}>
                <MenuItem value=""><em>— Single stream (no division)</em></MenuItem>
                {streams.map(s => <MenuItem key={s._id} value={s.streamCode}>{s.streamCode} — {s.streamName}</MenuItem>)}
              </TextField>
            </Grid>
            {!editing && derivedCode && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Class Code (auto-generated): <strong>{derivedCode}</strong>
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField label="Class Name *" name="className" value={form.className}
                onChange={handle} fullWidth size="small"
                helperText="e.g. Grade 1 East — auto-filled, you can edit" />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={employees}
                value={employees.find(e => e.employeeNo === form.classTeacher) || null}
                onChange={(_, val) => setForm(f => ({ ...f, classTeacher: val ? val.employeeNo : '' }))}
                renderInput={params => (
                  <TextField {...params} label="Class Teacher" size="small" fullWidth
                    helperText="Search by name or employee number" />
                )}
                isOptionEqualToValue={(o, v) => o.employeeNo === v.employeeNo}
              />
            </Grid>
            {editing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={form.isActive}
                    onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} />}
                  label={form.isActive ? 'Active' : 'Inactive'} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassList;
